import { isMobile } from '@proman/utils';
import { AppInitService } from "@proman/services/app-init.service";
import { MissingTranslationHandler, TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { CustomMissingTranslationHandler } from '@proman/shared/language/custom-missing-traslation-handler';
import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, ApplicationInitStatus, ErrorHandler, NgModule } from "@angular/core";
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { HttpRequestInterceptor } from '@proman/interceptors/http-request-interceptor';
import { HttpResponseInterceptor } from '@proman/interceptors/http-response-interceptor';
import { CustomDateAdapter } from '@proman/shared/adapters/custom-date.adapter';
import { DateAdapter, MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { AppComponent } from './app.component';
import { Observable, of } from 'rxjs';
import { ToastrModule } from 'ngx-toastr';
import { FlexLayoutModule } from 'ngx-flexible-layout';
import { CommonModule } from '@angular/common';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { EffectsModule } from '@ngrx/effects';
import { PosEffects } from '@pos-shared/store/pos.effects';
import { AppRoutingModule } from './app-routing.module';
import { Fa6Module } from '@proman/fa/fa6.module';
import { Entity } from '@proman/services/entity.service';
import { PosOrdersService } from '@pos-shared/services/pos-orders.service';
import { LocalStorageService } from '@proman/services/local-storage.service';
import { PosPaymentService } from '@pos-shared/services/pos-payment.service';
import { ParametersOptionsService } from '@proman/services/parameters-options.service';
import { PosLoginService } from '@pos-shared/services/pos-login.service';
import { PosStoreModule } from '@pos-shop-shared/store/pos-store.module';
import { PromanStoreModule } from '@proman/store/proman-store.module';
import { PromanModule } from '@proman/proman.module';
// import { Pos2AuthModule } from './auth/pos2-auth.module';
import { OverlayLoaderModule } from '@proman/overlay-loader';
import { Pos2Effects } from './store/pos2-effects';
import { ParametersStoreModule } from '@proman/parameters-store.module';
import { PosCardReaderService } from '@pos-shared/services/pos-card-reader.service';
import { PosCardLoginListenerService } from '@pos-shared/services/pos-card-login-listener.service';
import { MatLegacyProgressBarModule } from '@angular/material/legacy-progress-bar';
import { LoadingChunkFailedErrorHandler } from "@proman/error-handler/loading-chunk-failed-error.handler";
import { MatLegacyDialogModule } from '@angular/material/legacy-dialog';
import { AuthService } from '@proman/services/auth.service';
import { SentryModule } from '@proman/sentry';

export class CustomTranslateLoader implements TranslateLoader {
    translations: any;

    constructor(private AppInit: AppInitService) {
    }

    getTranslation(lang: string): Observable<any> {
        return of(this.AppInit.translations[this.AppInit.currentLang]);
    }

}

export function getTranslations(InitService: AppInitService ) {
    return () => InitService.initialize();
}

function getLocale(InitService: AppInitService): string {
    return InitService.currentLang;
}

const getToastPositionClass = (): string => {
    if (isMobile()) return 'toast-top-right';
    return 'toast-bottom-right';
}

@NgModule({
    imports: [
        BrowserModule,
        CommonModule,
        BrowserAnimationsModule,
        FlexLayoutModule,
        ToastrModule.forRoot({positionClass: getToastPositionClass(), preventDuplicates: true}),
        TranslateModule.forRoot({
            missingTranslationHandler: {
                provide: MissingTranslationHandler,
                useClass: CustomMissingTranslationHandler,
            },
            useDefaultLang: false,
        }),
        PromanModule,
        PromanStoreModule,
        PosStoreModule,
        ParametersStoreModule,
        EffectsModule.forFeature([PosEffects, Pos2Effects]),
        SentryModule.forRoot({ project: 'pos' }),
        OverlayLoaderModule,
        AppRoutingModule,
        Fa6Module,
        // NgxStarRatingModule,
        // Pos2AuthModule,
        // MatSidenavModule,
        MatLegacyDialogModule,
        MatLegacyProgressBarModule,
        MatNativeDateModule,
    ],
    declarations: [
        AppComponent,
    ],
    providers: [
        PosLoginService,
        Entity,
        LocalStorageService,
        PosOrdersService,
        AuthService,
        PosPaymentService,
        ParametersOptionsService,
        PosCardLoginListenerService,
        PosCardReaderService,
        AppInitService,
        { provide: APP_INITIALIZER, useFactory: getTranslations, deps: [AppInitService], multi: true },
        { provide: TranslateLoader, useClass: CustomTranslateLoader, deps: [AppInitService] },
        { provide: HTTP_INTERCEPTORS, useClass: HttpRequestInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: HttpResponseInterceptor, multi: true },
        { provide: DateAdapter, useClass: CustomDateAdapter },
        { provide: ErrorHandler, useClass: LoadingChunkFailedErrorHandler },
        { provide: MAT_DATE_LOCALE, useFactory: getLocale, deps: [AppInitService] },
    ],
    bootstrap: [
        AppComponent,
    ]
})
export class AppModule {
    constructor(applicationInitStatus: ApplicationInitStatus) {
        applicationInitStatus.donePromise.then(() => {
            const appLoader = document.querySelector('.loader-square-container');
            if (appLoader) setTimeout(() => {
                appLoader.remove();
            }, 250)
        });
    }
}
